import { store } from '@/store/store'
import { originalApi } from '@/store/api/originalApi'
import { paymentApi } from '@/store/api/paymentApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { addMinePendingTile, applyMineRoundState, selectMineGame, setMineAutoBetRunning, setMineAutoBetSettings, setMineSubmitting } from '@/store/slices/mineSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { extractApiErrorMessage } from '@/lib/api-message'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import type { MineGame, MineRoundState } from '@/types/mine'
import { MINE_BOARD_SIZE } from '@/config/original'
import { SoundManager } from './SoundManager'

const AUTO_BET_RESULT_DELAY_MS = 500
const AUTO_BET_ROUND_DELAY_MS = 500

export class GameController {
	private disposed = false
	private autoBetRunning = false
	private autoBetTimer: ReturnType<typeof setTimeout> | null = null
	private remainingAutoBets = 0
	private autoBetSessionProfit = 0

	constructor() {
		void this.loadActiveRound()
	}

	dispose() {
		this.disposed = true
		this.stopAutoBet()
	}

	private get mineState() {
		return selectMineGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private get mineGame(): MineGame | null {
		if (!this.mineState.mineGameId) {
			return null
		}

		return {
			id: this.mineState.mineGameId,
			currency_code: this.mineState.currencyCode,
			amount: Number(this.mineState.betAmount) || 0,
			mine_count: this.mineState.mineCount,
		}
	}

	private requireAuth(message: string) {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private clearAutoBetTimer() {
		if (this.autoBetTimer) {
			clearTimeout(this.autoBetTimer)
			this.autoBetTimer = null
		}
	}

	private applyRoundState(state: MineRoundState) {
		store.dispatch(applyMineRoundState(state))
		if (state.status === 'win') SoundManager.instance.play('win')
		if (state.status === 'lost') SoundManager.instance.play('mineExplosion')
		return
	}

	private async loadActiveRound() {
		if (!this.authenticated || this.disposed) {
			return
		}

		try {
			const response = await store.dispatch(originalApi.endpoints.getMineActiveRound.initiate()).unwrap()
			if (this.disposed) {
				return
			}

			const { mineGame, tiles, multiplier, payout } = response.data
			if (!mineGame) {
				return
			}

			store.dispatch(
				applyMineRoundState({
					status: 'bet',
					mineGame,
					tiles,
					multiplier,
					payout,
				}),
			)
		} catch (error) {
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to load the active mine game.')
		}
	}

	async startManualGame() {
		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const amount = Number(this.mineState.betAmount)
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		store.dispatch(setMineSubmitting(true))

		try {
			const response = await store
				.dispatch(
					originalApi.endpoints.startMineRound.initiate({
						currency_code: this.mineState.currencyCode,
						amount,
						mine_count: this.mineState.mineCount,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			SoundManager.instance.play('bet')
			this.applyRoundState({
				status: 'bet',
				mineGame: response.data.mineGame,
				tiles: [],
				multiplier: response.data.multiplier,
				payout: response.data.payout,
			})
		} catch (error) {
			store.dispatch(setMineSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to start the mine round.')
		}
	}

	async revealTile(point: number) {
		if (this.mineState.phase !== 'live' || this.mineState.isSubmitting) {
			return
		}

		if (this.mineState.revealedTiles.some((tile) => tile.point === point)) {
			return
		}

		const previousTiles = this.mineState.revealedTiles
		const mineGame = this.mineGame
		store.dispatch(addMinePendingTile(point))

		store.dispatch(setMineSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.revealMineTile.initiate({ point })).unwrap()
			const data = response.data

			if (data.status === 'bet') {
				SoundManager.instance.play('gemReveal')
				this.applyRoundState({
					status: 'bet',
					mineGame,
					tiles: data.tiles,
					multiplier: data.multiplier,
					payout: data.payout,
				})
				return
			} else if (data.status === 'win') {
				this.applyRoundState({
					status: 'win',
					mineGame: null,
					tiles: data.tiles,
					multiplier: data.result.multiplier,
					payout: data.result.payout,
					result: data.result,
				})
				return
			} else if (data.status === 'lost') {
				this.applyRoundState({
					status: 'lost',
					mineGame: null,
					tiles: data.tiles,
					multiplier: data.multiplier,
					payout: data.payout,
					result: data.result,
				})
			}
		} catch (error) {
			store.dispatch(
				applyMineRoundState({
					status: 'bet',
					mineGame,
					tiles: previousTiles,
					multiplier: this.mineState.currentMultiplier,
					payout: this.mineState.currentPayout,
				}),
			)
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to reveal tile.')
		}
	}

	async cashOut() {
		if (this.mineState.phase !== 'live' || this.mineState.isSubmitting) {
			return
		}

		const revealedCount = this.mineState.revealedTiles.filter((tile) => tile.isRevealed && tile.content === 'gem').length
		if (revealedCount === 0) {
			showApiErrorToast('Reveal at least one gem before cashing out.')
			return
		}

		store.dispatch(setMineSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.cashOutMineRound.initiate()).unwrap()
			const { status, tiles, result } = response.data

			this.applyRoundState({
				status,
				mineGame: null,
				tiles,
				multiplier: result.multiplier,
				payout: result.payout,
				result,
			})
		} catch (error) {
			store.dispatch(setMineSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to cash out.')
		}
	}

	pickRandomTile() {
		const revealedPoints = this.mineState.revealedTiles.map((tile) => tile.point)
		const available = Array.from({ length: MINE_BOARD_SIZE }, (_, index) => index).filter((point) => !revealedPoints.includes(point))

		if (available.length === 0) {
			return
		}

		const point = available[Math.floor(Math.random() * available.length)]
		void this.revealTile(point)
	}

	async startAutoBet() {
		if (this.autoBetRunning || this.mineState.isSubmitting) {
			return
		}

		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const points = this.mineState.autoSelectedPoints
		if (points.length === 0) {
			showApiErrorToast('Select at least one tile for auto bet.')
			return
		}

		const maxSafeTiles = MINE_BOARD_SIZE - this.mineState.mineCount
		if (points.length > maxSafeTiles) {
			showApiErrorToast(`Select at most ${maxSafeTiles} tiles for this mine count.`)
			return
		}

		this.autoBetRunning = true
		this.autoBetSessionProfit = 0
		this.remainingAutoBets = this.mineState.autoBetSettings.isInfinite ? Number.POSITIVE_INFINITY : this.mineState.autoBetSettings.betCount
		store.dispatch(setMineAutoBetRunning(true))
		this.scheduleAutoBetRound(AUTO_BET_ROUND_DELAY_MS)
	}

	stopAutoBet() {
		this.autoBetRunning = false
		this.autoBetSessionProfit = 0
		this.clearAutoBetTimer()
		store.dispatch(setMineAutoBetRunning(false))
		store.dispatch(setMineSubmitting(false))
	}

	private scheduleAutoBetRound(delayMs: number) {
		this.clearAutoBetTimer()
		this.autoBetTimer = setTimeout(() => {
			void this.runAutoBetRound()
		}, delayMs)
	}

	private async runAutoBetRound() {
		if (!this.autoBetRunning || this.disposed) {
			this.stopAutoBet()
			return
		}

		const amount = Number(this.mineState.betAmount)
		store.dispatch(setMineSubmitting(true))

		try {
			const response = await store
				.dispatch(
					originalApi.endpoints.placeMineAutoBet.initiate({
						currency_code: this.mineState.currencyCode,
						amount,
						mine_count: this.mineState.mineCount,
						points: [...this.mineState.autoSelectedPoints],
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			const { status, tiles, result } = response.data

			this.applyRoundState({
				status,
				mineGame: null,
				tiles,
				multiplier: result.multiplier,
				payout: result.payout,
				result,
			})

			if (!this.autoBetRunning) {
				return
			}

			this.autoBetSessionProfit += result.profit

			const { stopOnProfit, stopOnLoss, isInfinite } = this.mineState.autoBetSettings
			if (stopOnProfit > 0 && this.autoBetSessionProfit >= stopOnProfit) {
				this.stopAutoBet()
				return
			}

			if (stopOnLoss > 0 && this.autoBetSessionProfit <= -stopOnLoss) {
				this.stopAutoBet()
				return
			}

			if (!isInfinite) {
				this.remainingAutoBets -= 1
				store.dispatch(
					setMineAutoBetSettings({
						betCount: Math.max(0, this.remainingAutoBets),
					}),
				)

				if (this.remainingAutoBets <= 0) {
					this.stopAutoBet()
					return
				}
			}

			this.scheduleAutoBetRound(AUTO_BET_RESULT_DELAY_MS + AUTO_BET_ROUND_DELAY_MS)
		} catch (error) {
			this.stopAutoBet()
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to place auto bet.')
		}
	}
}
